import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  booleanAttribute,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ToggleComponent } from '../toggle/toggle.component';

@Component({
  selector: 'fleet-toggle-with-left-label-and-description',
  exportAs: 'fleetToggleWithLeft',
  host: {
    class: 'block min-w-0',
  },
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTooltipModule,
    ToggleComponent,
  ],
  template: ` <div class="flex items-center justify-between gap-3">
    <span
      class="flex flex-grow flex-col min-w-0 self-center"
      [class.cursor-pointer]="labelInteractable()"
      [attr.role]="labelInteractable() ? 'button' : null"
      [attr.tabindex]="labelInteractable() ? 0 : null"
      (click)="onLabelAreaClick($event)"
      (keydown.enter)="labelInteractable() ? toggle() : undefined"
      (keydown.space)="onLabelSpaceKey($event)"
    >
      @if (title) {
      <span
        class="leading-6 text-gray-900 dark:text-gray-100"
        [attr.id]="titleId"
        [ngClass]="titleSize + ' ' + titleFont"
        >{{ title }}</span
      >
      } @if (logo) {
      <div class="flex items-center justify-center w-20">
        <img
          [src]="logo"
          class="max-w-full max-h-full object-contain"
          onerror="this.style.display='none'"
        />
      </div>
      } @if (description) {
      <span
        class="text-sm text-gray-500 dark:text-gray-400 mt-0.5"
        [attr.id]="descriptionId"
        >{{ description }}</span
      >
      }
    </span>

    @if (!hideToggle) {
    <span class="flex shrink-0 items-center self-center leading-none">
      <fleet-toggle
        [formControl]="control"
        [size]="toggleSize"
        [toolTip]="toolTip"
      ></fleet-toggle>
    </span>
    }
  </div>`,
  styles: [],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleWithLeftLabelAndDescriptionComponent),
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
})
export class ToggleWithLeftLabelAndDescriptionComponent
  implements ControlValueAccessor, OnInit, OnChanges, OnDestroy
{
  @Input() titleSize = 'text-sm';
  @Input() titleFont = 'font-medium';
  @Input() title: string;

  /** Optional id on the title for `aria-labelledby` pairing. */
  @Input() titleId: string | null = null;
  /** Optional id on the description for `aria-describedby`. */
  @Input() descriptionId: string | null = null;

  @Input() logo: string;
  @Input() description: string;

  /**
   * Sync from parent when not using reactive `formControl` / `formControlName` /
   * `ngModel` on this component.
   *
   * Do not combine explicit `checked` with Angular forms bindings.
   */
  @Input() checked: boolean | undefined;

  /** Prefer `checked` — kept for backwards compatibility. */
  @Input() isChecked: boolean | undefined;

  @Input() toolTip: string;
  @Input() toggleSize: 'SMALL' | 'DEFAULT' | 'LARGE' = 'DEFAULT';
  @Input() hideToggle = false;

  @Input({ transform: booleanAttribute }) disabled = false;

  /**
   * When true (default), clicking the label/description column toggles the value
   * unless the toggle is hidden or disabled.
   */
  @Input({ transform: booleanAttribute }) clickableLabel = true;

  @Output() checkedChange = new EventEmitter<boolean>();

  control = new FormControl<boolean>(false);

  private readonly destroyed$ = new Subject<void>();

  private cvaDisabled = false;
  private onChange?: (value: boolean) => void;
  private onTouched?: () => void;

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.control.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value: boolean | null) => {
        const v = !!value;
        this.onChange?.(v);
        this.onTouched?.();
        this.checkedChange.emit(v);
      });
    this.applyDisabledState();
    this.syncFromExplicitCheckedInputs();
    this.changeDetectorRef.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('disabled' in changes || 'hideToggle' in changes) {
      this.applyDisabledState();
    }

    if (changes['checked'] || changes['isChecked']) {
      this.syncFromExplicitCheckedInputs();
    }
    this.changeDetectorRef.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * Programmatic toggle (parity with legacy `MatSlideToggle#toggle`).
   */
  toggle(): void {
    if (this.hideToggle || this.control.disabled) {
      return;
    }
    this.control.setValue(!this.control.value);
  }

  labelInteractable(): boolean {
    return !!this.clickableLabel && !this.hideToggle && !this.control.disabled;
  }

  onLabelAreaClick(ev: MouseEvent): void {
    if (!this.labelInteractable()) {
      return;
    }
    ev.preventDefault();
    this.toggle();
  }

  onLabelSpaceKey(ev: Event): void {
    if (!this.labelInteractable()) {
      return;
    }
    ev.preventDefault();
    this.toggle();
  }

  writeValue(value: boolean | null): void {
    this.control.setValue(!!value, { emitEvent: false });
    this.changeDetectorRef.markForCheck();
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled = isDisabled;
    this.applyDisabledState();
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Uses `checked` first, then `isChecked`.
   *
   * `undefined` for both preserves the current control value until `writeValue` / user toggle.
   */
  private syncFromExplicitCheckedInputs(): void {
    const v = this.readExplicitCheckedInputs();
    if (v.ready) {
      this.control.setValue(v.value, { emitEvent: false });
      this.changeDetectorRef.markForCheck();
    }
  }

  private readExplicitCheckedInputs(): { ready: boolean; value: boolean } {
    if (this.checked !== undefined) {
      return { ready: true, value: !!this.checked };
    }
    if (this.isChecked !== undefined) {
      return { ready: true, value: !!this.isChecked };
    }
    return { ready: false, value: false };
  }

  private applyDisabledState(): void {
    const off = this.cvaDisabled || this.disabled;
    if (off) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
    this.changeDetectorRef.markForCheck();
  }
}

@Component({
  selector: 'fleet-formly-toggle-with-left-label-and-description',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ToggleWithLeftLabelAndDescriptionComponent,
  ],
  template: `<fleet-toggle-with-left-label-and-description
    [formControl]="formControl"
  ></fleet-toggle-with-left-label-and-description>`,
  styles: [],
  encapsulation: ViewEncapsulation.None,
})
export class FormlyToggleWithLeftLabelAndDescriptionComponent
  extends FieldType<FieldTypeConfig>
  implements AfterViewInit
{
  @ViewChild(ToggleWithLeftLabelAndDescriptionComponent)
  toggleComponent: ToggleWithLeftLabelAndDescriptionComponent;
  constructor(private changeDetectorRef: ChangeDetectorRef) {
    super();
  }
  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.props?.toggleSize) {
        this.toggleComponent.toggleSize = this.props.toggleSize;
      }
      if (this.props?.label) {
        this.toggleComponent.title = this.props.label;
      }

      this.changeDetectorRef.markForCheck();
    }, 1);
  }
}
